import { supabase } from '../supabase';

// A parcel's re_pp is one of {Real Estate, Personal Property, Real and Personal}.
export const includesRE  = v => /real/i.test(v || '');
export const includesBPP = v => /personal/i.test(v || '');
export const scopeMatchesParcel = (scope, re_pp) =>
  scope === 'ALL' || (scope === 'RE' && includesRE(re_pp)) || (scope === 'BPP' && includesBPP(re_pp));

// Contacts effective for a parcel: its property's contact_properties whose re_pp_scope matches this parcel's re_pp,
// most-specific (RE/BPP) winning over ALL per role. Returns [{ role, scope, first_name, last_name, email }].
// (A 'Real and Personal' parcel matches BOTH RE and BPP scopes → both people surface — Key Contacts renders N per cell.)
//
// ⛔ SCOPE NOW GOVERNS CLIENT + DELIVERY ROLES ONLY (2026-08-16). The four firm-personnel EXPERT roles —
// consultant, re_appeal_agent, re_compliance, bpp_compliance — moved to parcel_contact_roles, which is
// parcel-grained and per value year, and were deleted from contact_properties by that migration. RE/BPP
// Compliance no longer resolve by matching the parcel's type: the ROLE NAME carries that meaning, and
// resolution does not key on re_pp. Attorney Contact is not firm personnel and stays here.
export async function resolveContactsForParcel(parcel) {
  if (!parcel?.property_id) return [];
  const { data, error } = await supabase.from('contact_properties')
    .select('re_pp_scope, role, contact:client_contacts(first_name,last_name,email)')
    .eq('property_id', parcel.property_id);
  if (error) return [];
  const matched = (data || []).filter(r => scopeMatchesParcel(r.re_pp_scope, parcel.re_pp));
  const byRole = {};
  matched.forEach(r => { (byRole[r.role] ||= []).push(r); });
  const out = [];
  Object.entries(byRole).forEach(([role, rs]) => {
    const specific = rs.filter(r => r.re_pp_scope !== 'ALL');   // a type-scoped assignment overrides ALL for its role
    (specific.length ? specific : rs).forEach(r => out.push({ role, scope: r.re_pp_scope, ...(r.contact || {}) }));
  });
  return out;
}

// All contacts assigned to a property, grouped BY PERSON (one entry per contact, with their assignments underneath —
// never one row per role/scope). Drives the read-only Property Contacts card.
export async function resolveContactsForProperty(propertyId) {
  if (!propertyId) return [];
  const { data } = await supabase.from('contact_properties')
    .select('re_pp_scope, role, contact_id, contact:client_contacts(id,first_name,last_name,email,title)')
    .eq('property_id', propertyId);
  const byContact = {};
  (data || []).forEach(r => {
    if (!r.contact) return;
    (byContact[r.contact_id] ||= { contact: r.contact, assignments: [] }).assignments.push({ scope: r.re_pp_scope, role: r.role });
  });
  return Object.values(byContact);
}
